/*
 * Issue #296: pure math for the Mortgage Insurance estimator. No DOM, no dependencies, CommonJS so
 * that webpack (imported from index.js/modal.js) and `node --test math.test.js` load the same file.
 *
 * LTV is compared in integer hundredths of a percent ("basis points" of LTV: 85.00% === 8500) so the
 * band edges are exact. In floating point, 340000 / 400000 * 100 is 85.00000000000001 and would fall
 * in the wrong band; rounding to an integer first makes 85.00 / 90.00 / 95.00 / 97.00 land on the
 * LOWER band (lower bound exclusive, upper bound inclusive), as AC-6 requires.
 */

const TIER_PERCENTS = [5, 10, 15, 20];

const ABOVE_MAX_LTV_MESSAGE = "We can't estimate mortgage insurance above 97% LTV. Enter your premium directly if you know it.";

/** Server bands [{lower, upper, rate}] (percent units) -> integer-bps bands, sorted, invalid rows dropped. */
function normalizeBands(rawBands) {
    if (!Array.isArray(rawBands)) return [];

    return rawBands
        .map((b) => ({
            lowerBps: Math.round(Number(b && b.lower) * 100),
            upperBps: Math.round(Number(b && b.upper) * 100),
            rate: Number(b && b.rate)
        }))
        .filter((b) => Number.isFinite(b.lowerBps) && Number.isFinite(b.upperBps) && Number.isFinite(b.rate)
            && b.rate >= 0 && b.upperBps > b.lowerBps)
        .sort((a, b) => a.lowerBps - b.lowerBps);
}

/** LTV in integer hundredths of a percent. Null when either input is not a positive number. */
function ltvBps(loan, value) {
    if (!(loan > 0) || !(value > 0)) return null;
    return Math.round((loan / value) * 10000);
}

/**
 * Looks up the rate for an LTV. status: 'notRequired' (at or below the lowest band's lower bound,
 * i.e. <= 80%), 'rate', or 'none' (above the highest band, or no band matches).
 */
function lookup(ltv, bands) {
    if (ltv === null || ltv === undefined || !bands.length) return { status: 'none', rate: null };

    if (ltv <= bands[0].lowerBps) return { status: 'notRequired', rate: 0 };

    const band = bands.find((b) => ltv > b.lowerBps && ltv <= b.upperBps);
    return band ? { status: 'rate', rate: band.rate } : { status: 'none', rate: null };
}

/**
 * Estimate for one loan at one LTV. annual = round(loan * rate%); monthly = round(UNROUNDED annual / 12),
 * not round(annual) / 12.
 */
function estimateAtLtv(loan, ltv, bands) {
    const found = lookup(ltv, bands);

    if (found.status === 'notRequired') {
        return { status: 'notRequired', ltvBps: ltv, rate: 0, annual: 0, monthly: 0 };
    }
    if (found.status !== 'rate') {
        return { status: 'none', ltvBps: ltv, rate: null, annual: null, monthly: null };
    }

    const annualExact = loan * found.rate / 100;
    return {
        status: 'rate',
        ltvBps: ltv,
        rate: found.rate,
        annual: Math.round(annualExact),
        monthly: Math.round(annualExact / 12)
    };
}

/** Estimate from a loan and a home value (Refinance). */
function estimate(loan, value, bands) {
    const ltv = ltvBps(loan, value);
    if (ltv === null) return { status: 'none', ltvBps: null, rate: null, annual: null, monthly: null };
    return estimateAtLtv(loan, ltv, bands);
}

/**
 * The 5/10/15/20% down-payment tiers for a home price. Each tier's LTV is exact (95/90/85/80), so the
 * band lookup is never subject to float error. Null when the price is not a positive number.
 */
function tiers(homePrice, bands) {
    if (!(homePrice > 0)) return null;

    return TIER_PERCENTS.map((pct) => {
        const downPayment = homePrice * pct / 100;
        const impliedLoan = homePrice - downPayment;
        const est = estimateAtLtv(impliedLoan, (100 - pct) * 100, bands);

        return {
            percent: pct,
            downPayment,
            impliedLoan,
            status: est.status,
            annual: est.annual,
            monthly: est.monthly
        };
    });
}

/**
 * The tier whose implied loan is closest to the visitor's loan. Ties go to the lower down payment
 * (tiers ascend, strict "<" keeps the first). The 20% tier takes part in the comparison but is never
 * returned: when it is the closest, nothing is highlighted (decision 10). Returns the percent or null.
 */
function closestTierPercent(loan, tierList) {
    if (!tierList || !(loan > 0)) return null;

    let best = null;
    let bestDelta = Infinity;
    tierList.forEach((t) => {
        const delta = Math.abs(t.impliedLoan - loan);
        if (delta < bestDelta) {
            best = t;
            bestDelta = delta;
        }
    });

    return best && best.percent !== 20 ? best.percent : null;
}

/** Whole-percent LTV for captions, rounded UP (80.3% -> 81). Works on integer bps to avoid float noise. */
function captionLtvPercent(ltv) {
    return Math.ceil(ltv / 100);
}

/** The write-back caption. kind: 'tier' (opts.percent = down %) or 'refinance' (opts.ltvBps, opts.notRequired). */
function captionFor(kind, opts) {
    if (kind === 'tier') {
        return opts.percent >= 20 ? 'MI not required with 20% down' : 'Estimated with ' + opts.percent + '% down';
    }

    const pct = captionLtvPercent(opts.ltvBps);
    return opts.notRequired ? 'MI not required at ' + pct + '% LTV' : 'Estimated at ' + pct + '% LTV';
}

/** Lenient numeric read of a field/result string ("$312,500", "7.000", "250,000.00"). NaN when empty/invalid. */
function parseNumber(text) {
    const cleaned = String(text === null || text === undefined ? '' : text).replace(/[$,%\s]/g, '');
    return /^-?(\d+\.?\d*|\.\d+)$/.test(cleaned) ? Number(cleaned) : NaN;
}

module.exports = {
    TIER_PERCENTS,
    ABOVE_MAX_LTV_MESSAGE,
    normalizeBands,
    ltvBps,
    lookup,
    estimateAtLtv,
    estimate,
    tiers,
    closestTierPercent,
    captionLtvPercent,
    captionFor,
    parseNumber
};
